import React, { useEffect } from "react"
import { useRecoilState } from "recoil"
import { ModalConfig, grayOverlayStyles, modalStyles, openTransition } from "../Types/Modals"
import { modalsAtom } from "../Atoms/Modals"
import styled from "styled-components"
import { useModalProvider } from "./ModalProvider"
import { useLocation } from "react-router-dom"
import { MobergFontFamily } from "../Components/MobergFont/MobergFont"
export const ModalRenderer = () => {
	const [modals, setModals] = useRecoilState<ModalConfig[]>(modalsAtom)
	const location = useLocation()
	const { close } = useModalProvider()

	const outsideClick = () => {
		if (!modals[modals.length - 1].component.props.clickOutsideClose) {
			return
		}

		close()
	}

	// Close all modals when you navigate away from the page.
	useEffect(() => {
		setModals([])
	}, [location, setModals])

	// Handle Animation for Opening Modals
	useEffect(() => {
		if (modals.length === 0) {
			return
		}

		// Timeout makes sure we definitely wait until after the render has finished.
		// This does not cause a delay because it is set to 0, but it pushes the execution of the animation
		// until immediately after the render has finished.
		setTimeout(() => {
			const config = modals[modals.length - 1]
			const modal = config.modalRef.current
			const overlay = config.overlayRef.current

			if (!modal) {
				return
			}

			modal.style.opacity = "1"
			modal.style.marginTop = "0"

			if (overlay) {
				overlay.style.transition = openTransition
				overlay.style.opacity = "0.4"
			}
		}, 0)
	}, [modals])

	// Prevent the page from scrolling when modals are open
	useEffect(() => {
		if (modals.length === 0) {
			return
		}

		const nonModalContent = document.getElementById("HeaderAndPage")

		if (nonModalContent) {
			nonModalContent.style.overflow = "hidden"
		}

		return () => {
			if (nonModalContent) {
				nonModalContent.style.overflow = "auto"
			}
		}
		
	}, [modals])

	const defaultStackingBehaviorModals = modals.filter(({ options }) => options.zIndex === undefined)
	const controlledStackingBehaviorModals = modals.filter(({ options }) => options.zIndex !== undefined)

	const getModalAndOverlay = (modalConfig: ModalConfig, index: number) => {
		const modalId = `Modal ${index}`
		const overlayId = modalId + " GrayOverlay"

		const { component, modalRef, overlayRef, options } = modalConfig

		return [
			<Modal
				id={modalId}
				key={modalId}
				ref={modalRef}
				style={{ ...modalStyles, ...component.props.style, transition: options?.animate ? openTransition : "none", zIndex: index + 2, pointerEvents: "auto" }}
			>
				{component}
			</Modal>,
			<div 
				id={overlayId}
				key={overlayId}
				ref={overlayRef}
				style={{ ...grayOverlayStyles, zIndex: index + 1, pointerEvents: "auto" }} onClick={outsideClick} />,
		]
	}

	return (
		<>
			{ defaultStackingBehaviorModals.length > 0 && (
				<div id="ModalsContainer" style={{ position: "fixed", zIndex: 10000, inset: 0, display: "flex", justifyContent: "center", alignItems: "center", pointerEvents: "none" }}>
					{modals.map((modalConfig, index) => getModalAndOverlay(modalConfig, index))}
				</div>
			)}

			{ controlledStackingBehaviorModals.length > 0 && (
				controlledStackingBehaviorModals.map((modalConfig, index) => (
					<div style={{ position: "fixed", zIndex: modalConfig.options.zIndex || 10000, inset: 0, display: "flex", justifyContent: "center", alignItems: "center", pointerEvents: "none" }}>
						{getModalAndOverlay(modalConfig, index)}
					</div>
				))
			)}
		</>
	)
}

const Modal = styled.div`
    width: 820px;
	height: auto;
    background: var(--color-surface-panel);
    border-radius: 6px;
    h3 {
        font-family: ${MobergFontFamily.HEADER};
        font-style: normal;
        font-weight: 700;
        font-size: 20px;
        line-height: 150%;
        color: var(--color-text-primary);
        text-align: center;
        margin-top: 8px;
        margin-bottom: -4px;
    }
    h2 {
        font-family: ${MobergFontFamily.HEADER};
        font-style: normal;
        font-weight: 700;
        font-size: 21px;
        line-height: 150%;
        color: var(--color-text-primary);
        margin-top: 5px;
    }
    h1 {
        font-family: ${MobergFontFamily.HEADER};
        font-style: normal;
        font-weight: 600;
        font-size: 16px;
        line-height: 150%;
        color: var(--color-text-primary);
    }
`;
